/**
 * Enumeration of all animatable pane state attributes.
 *
 * Port of C++ PaneStateAttribute from tabanim/public/animation/PaneStateAttribute.h
 *
 * In C++, these are bitflags (1 << n) that can be combined with bitwise OR.
 * In TypeScript, we use a simple string enum for easier usage and type safety.
 *
 * Attributes are organized into categories:
 * - Mark attributes: positions, size, color, weight, angles
 * - Label attributes: position and dimensions
 * - Pane bounds: rectangles, domains, origins, ranges
 * - Grid attributes: origin, spacing, colors
 * - User pane attributes: cell/pixel offsets, widths, heights
 * - Heatmap attributes: min/max values
 * - Axis attributes: min/max, colors, sort direction
 */
export enum PaneStateAttribute {
  // No attributes
  NoAttributes = 'NoAttributes',

  // Mark position attributes
  MarkXPosition = 'MarkXPosition',
  MarkYPosition = 'MarkYPosition',
  MarkXFarPosition = 'MarkXFarPosition',
  MarkYFarPosition = 'MarkYFarPosition',
  MarkXPrev = 'MarkXPrev',
  MarkYPrev = 'MarkYPrev',
  MarkXNext = 'MarkXNext',
  MarkYNext = 'MarkYNext',

  // Mark visual attributes
  MarkSize = 'MarkSize',
  MarkColor = 'MarkColor',
  MarkWeight = 'MarkWeight',
  HeatmapMarkWeight = 'HeatmapMarkWeight',

  // Mark angle attributes (rotation)
  Theta = 'Theta',
  Theta1 = 'Theta1',

  // Pane rectangle bounds
  PaneRectBottom = 'PaneRectBottom',
  PaneRectTop = 'PaneRectTop',
  PaneRectLeft = 'PaneRectLeft',
  PaneRectRight = 'PaneRectRight',

  // Pane domain bounds
  PaneRectDomainBottom = 'PaneRectDomainBottom',
  PaneRectDomainTop = 'PaneRectDomainTop',
  PaneRectDomainLeft = 'PaneRectDomainLeft',
  PaneRectDomainRight = 'PaneRectDomainRight',

  // Pane origin
  PaneRectOriginX = 'PaneRectOriginX',
  PaneRectOriginY = 'PaneRectOriginY',

  UserPaneXCellOffsets = 'UserPaneXCellOffsets',
  UserPaneYCellOffsets = 'UserPaneYCellOffsets',
  UserPaneXPixelOffsets = 'UserPaneXPixelOffsets',
  UserPaneYPixelOffsets = 'UserPaneYPixelOffsets',
  UserPaneXCellWidths = 'UserPaneXCellWidths',
  UserPaneYCellHeights = 'UserPaneYCellHeights',
  UserPaneXPixelWidths = 'UserPaneXPixelWidths',
  UserPaneYPixelHeights = 'UserPaneYPixelHeights',

  // Heatmap attributes
  HeatmapMinValue = 'HeatmapMinValue',
  HeatmapMaxValue = 'HeatmapMaxValue',

  // Grid attributes
  GridOrigin = 'GridOrigin',
  GridSpacing = 'GridSpacing',
  GridXLineColor = 'GridXLineColor',
  GridYLineColor = 'GridYLineColor',
  GridZeroPoint = 'GridZeroPoint',

  // Label attributes
  LabelX = 'LabelX',
  LabelY = 'LabelY',
  LabelWidth = 'LabelWidth',
  LabelHeight = 'LabelHeight',

  // Range attributes
  XRangeMin = 'XRangeMin',
  XRangeMax = 'XRangeMax',
  YRangeMin = 'YRangeMin',
  YRangeMax = 'YRangeMax',

  // Map domain
  MapDomainBottom = 'MapDomainBottom',
  MapDomainTop = 'MapDomainTop',
  MapDomainLeft = 'MapDomainLeft',
  MapDomainRight = 'MapDomainRight',

  // Axis attributes
  XAxisDividerColor = 'XAxisDividerColor',
  XAxisTickColorIndex = 'XAxisTickColorIndex',
  XSortDirection = 'XSortDirection',
  YAxisDividerColor = 'YAxisDividerColor',
  YAxisTickColorIndex = 'YAxisTickColorIndex',
  YSortDirection = 'YSortDirection',
  AxisTickColor = 'AxisTickColor',
  XAxisMin = 'XAxisMin',
  XAxisMax = 'XAxisMax',
  YAxisMin = 'YAxisMin',
  YAxisMax = 'YAxisMax',
}

/**
 * Utility class for working with PaneStateAttribute.
 * Provides helper methods for categorizing attributes.
 */
export class PaneStateAttributeUtil {
  /**
   * Get all mark-related attributes (position, size, color, weight, angles).
   */
  static getAllMarkAttributes(): PaneStateAttribute[] {
    return [
      PaneStateAttribute.MarkXPosition,
      PaneStateAttribute.MarkYPosition,
      PaneStateAttribute.MarkXFarPosition,
      PaneStateAttribute.MarkYFarPosition,
      PaneStateAttribute.MarkXPrev,
      PaneStateAttribute.MarkYPrev,
      PaneStateAttribute.MarkXNext,
      PaneStateAttribute.MarkYNext,
      PaneStateAttribute.MarkSize,
      PaneStateAttribute.MarkColor,
      PaneStateAttribute.MarkWeight,
      PaneStateAttribute.HeatmapMarkWeight,
      PaneStateAttribute.Theta,
      PaneStateAttribute.Theta1,
    ];
  }

  /**
   * Get mark attributes allowed for map animation when position should not animate.
   * C++ Reference: ShouldPreventAnimationOfMapPosition() restricts to MarkSize | MarkColor | MarkWeight | HeatmapMarkWeight
   */
  static getMapAllowedMarkAttributes(): PaneStateAttribute[] {
    return [PaneStateAttribute.MarkSize, PaneStateAttribute.MarkColor, PaneStateAttribute.MarkWeight, PaneStateAttribute.HeatmapMarkWeight];
  }

  /**
   * Get all label-related attributes (position and dimensions).
   */
  static getAllLabelAttributes(): PaneStateAttribute[] {
    return [PaneStateAttribute.LabelX, PaneStateAttribute.LabelY, PaneStateAttribute.LabelWidth, PaneStateAttribute.LabelHeight];
  }

  /**
   * Get all pane bounds attributes (rectangles, domains, origins, ranges).
   */
  static getAllPaneBoundsAttributes(): PaneStateAttribute[] {
    return [
      PaneStateAttribute.PaneRectBottom,
      PaneStateAttribute.PaneRectTop,
      PaneStateAttribute.PaneRectLeft,
      PaneStateAttribute.PaneRectRight,
      PaneStateAttribute.PaneRectDomainBottom,
      PaneStateAttribute.PaneRectDomainTop,
      PaneStateAttribute.PaneRectDomainLeft,
      PaneStateAttribute.PaneRectDomainRight,
      PaneStateAttribute.PaneRectOriginX,
      PaneStateAttribute.PaneRectOriginY,
      PaneStateAttribute.XRangeMin,
      PaneStateAttribute.XRangeMax,
      PaneStateAttribute.YRangeMin,
      PaneStateAttribute.YRangeMax,
      PaneStateAttribute.MapDomainBottom,
      PaneStateAttribute.MapDomainTop,
      PaneStateAttribute.MapDomainLeft,
      PaneStateAttribute.MapDomainRight,
    ];
  }

  /**
   * Get all grid-related attributes (origin, spacing, colors).
   */
  static getAllGridAttributes(): PaneStateAttribute[] {
    return [...this.getGridPerLineAttributes(), PaneStateAttribute.GridXLineColor, PaneStateAttribute.GridYLineColor];
  }

  static getGridPerLineAttributes(): PaneStateAttribute[] {
    return [PaneStateAttribute.GridOrigin, PaneStateAttribute.GridSpacing, PaneStateAttribute.GridZeroPoint];
  }

  static getAllUserPaneAttributes(): PaneStateAttribute[] {
    return [
      PaneStateAttribute.UserPaneXCellOffsets,
      PaneStateAttribute.UserPaneYCellOffsets,
      PaneStateAttribute.UserPaneXPixelOffsets,
      PaneStateAttribute.UserPaneYPixelOffsets,
      PaneStateAttribute.UserPaneXCellWidths,
      PaneStateAttribute.UserPaneXPixelWidths,
      PaneStateAttribute.UserPaneYCellHeights,
      PaneStateAttribute.UserPaneYPixelHeights,
    ];
  }

  /**
   * Get all heatmap non-mark attributes (min/max values).
   */
  static getAllHeatmapNonMarkAttributes(): PaneStateAttribute[] {
    return [PaneStateAttribute.HeatmapMinValue, PaneStateAttribute.HeatmapMaxValue];
  }

  /**
   * Get all axis-related attributes (min/max, colors, sort direction).
   */
  static getAllAxisAttributes(): PaneStateAttribute[] {
    return [
      PaneStateAttribute.XAxisMin,
      PaneStateAttribute.XAxisMax,
      PaneStateAttribute.XAxisDividerColor,
      PaneStateAttribute.YAxisMin,
      PaneStateAttribute.YAxisMax,
      PaneStateAttribute.YAxisDividerColor,
      PaneStateAttribute.AxisTickColor,
      PaneStateAttribute.XSortDirection,
      PaneStateAttribute.YSortDirection,
    ];
  }

  /**
   * Get all non-mark attributes.
   */
  static getAllNonMarkAttributes(): PaneStateAttribute[] {
    return [
      ...this.getAllPaneBoundsAttributes(),
      ...this.getAllGridAttributes(),
      ...this.getAllUserPaneAttributes(),
      ...this.getAllHeatmapNonMarkAttributes(),
      ...this.getAllAxisAttributes(),
    ];
  }

  /**
   * Get all attributes.
   */
  static getAllAttributes(): PaneStateAttribute[] {
    return [...this.getAllMarkAttributes(), ...this.getAllNonMarkAttributes(), ...this.getAllLabelAttributes()];
  }

  /**
   * Check if an attribute requires angle interpolation (handles 360° wrapping).
   */
  static isAngleAttribute(attribute: PaneStateAttribute): boolean {
    return attribute === PaneStateAttribute.Theta || attribute === PaneStateAttribute.Theta1;
  }

  /**
   * Check if an attribute requires weight interpolation (fade to 0 for enter/exit).
   * C++ Reference: GetWeightAnimationAttributes() returns MarkWeight | HeatmapMarkWeight.
   */
  static isWeightAttribute(attribute: PaneStateAttribute): boolean {
    return attribute === PaneStateAttribute.MarkWeight || attribute === PaneStateAttribute.HeatmapMarkWeight;
  }

  /**
   * Check if an attribute requires color interpolation (per-channel ARGB).
   */
  static isColorAttribute(attribute: PaneStateAttribute): boolean {
    return (
      attribute === PaneStateAttribute.MarkColor ||
      attribute === PaneStateAttribute.GridXLineColor ||
      attribute === PaneStateAttribute.GridYLineColor ||
      attribute === PaneStateAttribute.XAxisDividerColor ||
      attribute === PaneStateAttribute.YAxisDividerColor ||
      attribute === PaneStateAttribute.AxisTickColor
    );
  }
}
